import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight } from "lucide-react";

export default function PublicHeader() {
  const { t, lang, toggle } = useI18n();
  const { user } = useAuth();
  const { pathname } = useLocation();

  const navItem = (to, label, hash) => {
    const isActive = hash ? false : pathname === to;
    return (
      <Link
        key={to + (hash || "")}
        to={hash ? `${to}${hash}` : to}
        className={`link-underline ${isActive ? "text-[#0B1E3F] font-bold" : "text-[#0B1E3F]/70"}`}
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {label}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-50 bg-transparent backdrop-blur-md" data-testid="landing-header">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-1 flex-shrink-0" data-testid="brand-logo">
          <span className="font-display font-black text-3xl tracking-tighter text-white inline-flex items-center">
            <span className="border border-white px-1.5 py-0.5 mr-[2px] leading-none">M</span><span>aqam</span>
          </span>
        </Link>
        <nav className="hidden lg:flex items-center gap-7 text-sm font-medium">
          <Link to="/#features" className="link-underline text-white/80 hover:text-white" data-testid="nav-features">Features</Link>
          <Link to="/subscriptions" className="link-underline text-white/80 hover:text-white" data-testid="nav-subscriptions">Subscriptions</Link>
          <Link to="/news" className="link-underline text-white/80 hover:text-white" data-testid="nav-news">News</Link>
          <Link to="/study-abroad" className="link-underline text-white/80 hover:text-white" data-testid="nav-study-abroad">Study Abroad</Link>
          <Link to="/tips" className="link-underline text-white/80 hover:text-white" data-testid="nav-tips">Tips</Link>
          <Link to="/become-one-of-us" className="link-underline font-bold text-white" data-testid="nav-become">
            Become one of us
          </Link>
          <Link to="/hire" className="link-underline text-white/50 hover:text-white text-xs uppercase tracking-widest" data-testid="nav-employers">
            For employers
          </Link>
          <Link to="/agencies" className="link-underline text-white/50 hover:text-white text-xs uppercase tracking-widest" data-testid="nav-agencies">
            For agencies
          </Link>
          <Link to="/for-investors" className="link-underline text-white/50 hover:text-white text-xs uppercase tracking-widest" data-testid="nav-investors">
            For investors
          </Link>
        </nav>
        <div className="flex items-center gap-4 flex-shrink-0">
          <button
            onClick={toggle}
            className="font-sans text-xs uppercase tracking-widest px-3 py-2 border border-white/20 text-white/80 hover:bg-white hover:text-[#1a1a1c] transition-colors"
            data-testid="lang-toggle"
          >
            {lang === "en" ? "عربي" : "EN"}
          </button>
          {user ? (
            <Link
              to="/app/dashboard"
              className="bg-white text-[#1a1a1c] hover:bg-white/90 hover:text-[#1a1a1c] text-sm font-bold px-6 py-3 transition-colors flex items-center gap-2 border border-white"
              data-testid="dashboard-link-header"
            >
              Dashboard <ArrowRight className="w-4 h-4" />
            </Link>
          ) : (
            <>
              <Link to="/login" className="hidden sm:inline-block text-sm font-medium px-4 py-2 hover:bg-white/10 text-white" data-testid="login-link">
                {t("login")}
              </Link>
              <Link
                to="/register"
                className="text-sm font-bold px-6 py-3 text-white hover:text-white/70 transition-colors flex items-center gap-2"
                data-testid="register-link-header"
              >
                {t("sign_up")} <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          )}
        </div>
      </div>
      {/* Mobile nav */}
      <nav className="lg:hidden flex overflow-x-auto gap-5 px-6 pb-4 text-sm font-medium hide-scrollbar">
        <Link to="/#features" className="text-white/80">Features</Link>
        <Link to="/subscriptions" className="text-white/80">Subscriptions</Link>
        <Link to="/news" className="text-white/80">News</Link>
        <Link to="/study-abroad" className="text-white/80">Study Abroad</Link>
        <Link to="/tips" className="text-white/80">Tips</Link>
        <Link to="/become-one-of-us" className="text-white font-bold">Become one of us</Link>
      </nav>
    </header>
  );
}
